/**
 * Utilidades de Cálculos Laborales Chile
 * Funciones puras para cálculos relacionados con el derecho laboral chileno
 */

/**
 * Calcula la antigüedad exacta entre dos fechas
 * @param {Date|string} fechaIngreso - Fecha de inicio de la relación laboral
 * @param {Date|string} fechaTermino - Fecha de término
 * @returns {Object} - { anios, meses, dias, totalDias }
 */
function calcularAntiguedadExacta(fechaIngreso, fechaTermino) {
    const inicio = new Date(fechaIngreso);
    const fin = new Date(fechaTermino);

    // Calcular diferencia en días totales
    const diffTime = Math.abs(fin - inicio);
    const totalDias = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Calcular años, meses y días
    let anios = fin.getFullYear() - inicio.getFullYear();
    let meses = fin.getMonth() - inicio.getMonth();
    let dias = fin.getDate() - inicio.getDate();

    // Ajustar si los días son negativos
    if (dias < 0) {
        meses--;
        const ultimoDiaMesAnterior = new Date(fin.getFullYear(), fin.getMonth(), 0).getDate();
        dias += ultimoDiaMesAnterior;
    }

    // Ajustar si los meses son negativos
    if (meses < 0) {
        anios--;
        meses += 12;
    }

    return {
        anios,
        meses,
        dias,
        totalDias,
        aniosDecimal: totalDias / 365.25
    };
}

/**
 * Calcula los años completos de servicio para indemnización
 * Según Art. 163: fracción superior a 6 meses se considera año completo
 * @param {Date|string} fechaIngreso - Fecha de inicio
 * @param {Date|string} fechaTermino - Fecha de término
 * @returns {number} - Años completos para indemnización
 */
function calcularAniosCompletos(fechaIngreso, fechaTermino) {
    const antiguedad = calcularAntiguedadExacta(fechaIngreso, fechaTermino);

    // Si tiene más de 6 meses de fracción, se redondea hacia arriba
    if (antiguedad.meses > 6 || (antiguedad.meses === 6 && antiguedad.dias > 0)) {
        return antiguedad.anios + 1;
    }

    return antiguedad.anios;
}

/**
 * Calcula la fracción de meses trabajados
 * @param {Date|string} fechaIngreso - Fecha de inicio
 * @param {Date|string} fechaTermino - Fecha de término
 * @returns {number} - Meses fraccionados (ej: 2.5 meses)
 */
function calcularMesesFraccion(fechaIngreso, fechaTermino) {
    const antiguedad = calcularAntiguedadExacta(fechaIngreso, fechaTermino);
    return antiguedad.anios * 12 + antiguedad.meses + (antiguedad.dias / 30);
}

/**
 * Calcula los días entre dos fechas
 * @param {Date|string} fechaInicio - Fecha de inicio
 * @param {Date|string} fechaFin - Fecha de fin
 * @returns {number} - Número de días
 */
function calcularDiasEntreFechas(fechaInicio, fechaFin) {
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    const diffTime = Math.abs(fin - inicio);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Calcula el valor de un día de trabajo
 * @param {number} remuneracionMensual - Remuneración mensual
 * @param {string} tipoDia - 'CORRIDO' (30 días) o 'HABIL' (20 días promedio)
 * @returns {number} - Valor del día
 */
function calcularValorDia(remuneracionMensual, tipoDia = 'CORRIDO') {
    const divisor = tipoDia === 'HABIL' ? 20 : 30;
    return Math.round(remuneracionMensual / divisor);
}

/**
 * Calcula el valor de una hora de trabajo
 * @param {number} remuneracionMensual - Remuneración mensual
 * @param {number} horasSemanales - Horas de jornada semanal (default 45)
 * @returns {number} - Valor de la hora
 */
function calcularValorHora(remuneracionMensual, horasSemanales = 45) {
    // Horas mensuales = horas semanales * 4.33 (semanas promedio por mes)
    const horasMensuales = horasSemanales * 4.33;
    return Math.round(remuneracionMensual / horasMensuales);
}

/**
 * Calcula la remuneración proporcional por días trabajados
 * @param {number} remuneracionMensual - Remuneración mensual completa
 * @param {number} diasTrabajados - Días efectivamente trabajados
 * @param {number} diasMes - Días del mes (default 30)
 * @returns {number} - Monto proporcional
 */
function calcularRemuneracionProporcional(remuneracionMensual, diasTrabajados, diasMes = 30) {
    return Math.round((remuneracionMensual / diasMes) * diasTrabajados);
}

/**
 * Calcula la gratificación mensual según Art. 50 del Código del Trabajo
 * 25% de la remuneración mensual con tope de 4.75 Ingresos Mínimos Mensuales
 * @param {number} sueldoBase - Sueldo base mensual
 * @param {number} ingresoMinimo - Ingreso mínimo mensual vigente (default 460.000 para 2024)
 * @returns {number} - Monto de gratificación mensual
 */
function calcularGratificacionMensualArt50(sueldoBase, ingresoMinimo = 460000) {
    const gratificacion25 = Math.round(sueldoBase * 0.25);
    const tope475IMM = Math.round((4.75 * ingresoMinimo) / 12); // Mensualizado

    return Math.min(gratificacion25, tope475IMM);
}

/**
 * Calcula la gratificación proporcional por días trabajados
 * @param {number} sueldoBase - Sueldo base mensual
 * @param {number} diasTrabajados - Días trabajados en el mes
 * @param {number} ingresoMinimo - Ingreso mínimo mensual vigente
 * @returns {number} - Gratificación proporcional
 */
function calcularGratificacionProporcional(sueldoBase, diasTrabajados, ingresoMinimo = 460000) {
    const gratificacionMensual = calcularGratificacionMensualArt50(sueldoBase, ingresoMinimo);
    return Math.round((gratificacionMensual / 30) * diasTrabajados);
}

/**
 * Determina si una causal de término da derecho a indemnización
 * @param {string} causalTermino - Código de la causal (ej: 'ART_161_1')
 * @returns {Object} - { tieneDerechoIndemnizacion, tieneDerechoMesAviso, motivo }
 */
function determinarDerechosSegunCausal(causalTermino) {
    const causalesConIndemnizacion = {
        'ART_161_1': { nombre: 'Necesidades de la empresa', indemnizacion: true, mesAviso: true },
        'ART_161_2': { nombre: 'Desahucio del empleador', indemnizacion: true, mesAviso: true }
    };

    const causalesSinIndemnizacion = {
        'ART_159_1': { nombre: 'Mutuo acuerdo de las partes', indemnizacion: false, mesAviso: false },
        'ART_159_2': { nombre: 'Renuncia del trabajador', indemnizacion: false, mesAviso: false },
        'ART_159_3': { nombre: 'Muerte del trabajador', indemnizacion: false, mesAviso: false },
        'ART_159_4': { nombre: 'Vencimiento del plazo convenido', indemnizacion: false, mesAviso: false },
        'ART_159_5': { nombre: 'Conclusión del trabajo o servicio', indemnizacion: false, mesAviso: false },
        'ART_159_6': { nombre: 'Caso fortuito o fuerza mayor', indemnizacion: false, mesAviso: false },
        'ART_160_1': { nombre: 'Falta de probidad, vías de hecho, injurias', indemnizacion: false, mesAviso: false },
        'ART_160_2': { nombre: 'Negociaciones prohibidas', indemnizacion: false, mesAviso: false },
        'ART_160_3': { nombre: 'Inasistencia injustificada', indemnizacion: false, mesAviso: false },
        'ART_160_4': { nombre: 'Abandono del trabajo', indemnizacion: false, mesAviso: false },
        'ART_160_5': { nombre: 'Actos, omisiones o imprudencias temerarias', indemnizacion: false, mesAviso: false },
        'ART_160_6': { nombre: 'Perjuicio material causado intencionalmente', indemnizacion: false, mesAviso: false },
        'ART_160_7': { nombre: 'Incumplimiento grave de obligaciones', indemnizacion: false, mesAviso: false }
    };

    const causal = causalesConIndemnizacion[causalTermino] || causalesSinIndemnizacion[causalTermino];

    if (!causal) {
        return {
            tieneDerechoIndemnizacion: false,
            tieneDerechoMesAviso: false,
            motivo: 'Causal no reconocida',
            nombreCausal: 'Desconocida'
        };
    }

    return {
        tieneDerechoIndemnizacion: causal.indemnizacion,
        tieneDerechoMesAviso: causal.mesAviso,
        motivo: causal.indemnizacion ? null : `Causal ${causalTermino} no da derecho a indemnización`,
        nombreCausal: causal.nombre
    };
}

/**
 * Obtiene todas las causales de término disponibles
 * @returns {Array} - Lista de causales con sus códigos y nombres
 */
function obtenerCausalesTermino() {
    return [
        { codigo: 'ART_159_1', nombre: 'Mutuo acuerdo de las partes', articulo: '159 N°1' },
        { codigo: 'ART_159_2', nombre: 'Renuncia del trabajador', articulo: '159 N°2' },
        { codigo: 'ART_159_3', nombre: 'Muerte del trabajador', articulo: '159 N°3' },
        { codigo: 'ART_159_4', nombre: 'Vencimiento del plazo convenido', articulo: '159 N°4' },
        { codigo: 'ART_159_5', nombre: 'Conclusión del trabajo o servicio', articulo: '159 N°5' },
        { codigo: 'ART_159_6', nombre: 'Caso fortuito o fuerza mayor', articulo: '159 N°6' },
        { codigo: 'ART_160_1', nombre: 'Falta de probidad, vías de hecho, injurias', articulo: '160 N°1' },
        { codigo: 'ART_160_2', nombre: 'Negociaciones prohibidas', articulo: '160 N°2' },
        { codigo: 'ART_160_3', nombre: 'Inasistencia injustificada', articulo: '160 N°3' },
        { codigo: 'ART_160_4', nombre: 'Abandono del trabajo', articulo: '160 N°4' },
        { codigo: 'ART_160_5', nombre: 'Actos, omisiones o imprudencias temerarias', articulo: '160 N°5' },
        { codigo: 'ART_160_6', nombre: 'Perjuicio material causado intencionalmente', articulo: '160 N°6' },
        { codigo: 'ART_160_7', nombre: 'Incumplimiento grave de obligaciones', articulo: '160 N°7' },
        { codigo: 'ART_161_1', nombre: 'Necesidades de la empresa', articulo: '161' },
        { codigo: 'ART_161_2', nombre: 'Desahucio del empleador', articulo: '161' }
    ];
}

/**
 * Formatea un monto en pesos chilenos
 * @param {number} monto - Monto a formatear
 * @returns {string} - Monto formateado (ej: "1.234.567")
 */
function formatearMontoCLP(monto) {
    if (monto === null || monto === undefined) return '0';
    return Math.round(monto).toLocaleString('es-CL');
}

/**
 * Formatea un RUT chileno
 * @param {string} rut - RUT sin formato
 * @returns {string} - RUT formateado (ej: "12.345.678-9")
 */
function formatearRUT(rut) {
    if (!rut) return '';

    // Limpiar el RUT
    const rutLimpio = rut.replace(/[^0-9kK]/g, '').toUpperCase();

    if (rutLimpio.length < 2) return rut;

    const cuerpo = rutLimpio.slice(0, -1);
    const dv = rutLimpio.slice(-1);

    // Formatear con puntos
    let cuerpoFormateado = '';
    for (let i = cuerpo.length - 1, j = 0; i >= 0; i--, j++) {
        if (j > 0 && j % 3 === 0) {
            cuerpoFormateado = '.' + cuerpoFormateado;
        }
        cuerpoFormateado = cuerpo[i] + cuerpoFormateado;
    }

    return `${cuerpoFormateado}-${dv}`;
}

/**
 * Formatea una fecha en formato chileno
 * @param {Date|string} fecha - Fecha a formatear
 * @returns {string} - Fecha formateada (ej: "15 de enero de 2024")
 */
function formatearFechaChilena(fecha) {
    if (!fecha) return '';
    const d = new Date(fecha);
    return d.toLocaleDateString('es-CL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
}

/**
 * Formatea una fecha corta
 * @param {Date|string} fecha - Fecha a formatear
 * @returns {string} - Fecha formateada (ej: "15/01/2024")
 */
function formatearFechaCorta(fecha) {
    if (!fecha) return '';
    const d = new Date(fecha);
    return d.toLocaleDateString('es-CL');
}

/**
 * Calcula el tope de 90 UF en pesos
 * @param {number} valorUF - Valor de la UF
 * @returns {number} - Tope en pesos
 */
function calcularTope90UF(valorUF) {
    return Math.round(90 * valorUF);
}

/**
 * Aplica el tope de 90 UF a un monto de remuneración
 * @param {number} remuneracion - Remuneración mensual
 * @param {number} valorUF - Valor de la UF
 * @returns {Object} - { montoOriginal, montoConTope, topeAplicado }
 */
function aplicarTope90UF(remuneracion, valorUF) {
    const tope = calcularTope90UF(valorUF);
    const topeAplicado = remuneracion > tope;

    return {
        montoOriginal: remuneracion,
        montoConTope: topeAplicado ? tope : remuneracion,
        topeAplicado,
        tope90UFPesos: tope
    };
}

/**
 * Calcula los días de vacaciones devengados según antigüedad
 * Según Art. 67: 15 días hábiles por año trabajado
 * @param {number} aniosTrabajados - Años trabajados (puede ser decimal)
 * @returns {number} - Días de vacaciones devengados
 */
function calcularDiasVacacionesDevengados(aniosTrabajados) {
    return Math.round(aniosTrabajados * 15 * 100) / 100;
}

/**
 * Calcula el monto de vacaciones pendientes
 * @param {number} diasPendientes - Días de vacaciones pendientes
 * @param {number} remuneracionMensual - Remuneración mensual
 * @returns {number} - Monto de vacaciones
 */
function calcularMontoVacaciones(diasPendientes, remuneracionMensual) {
    const valorDia = calcularValorDia(remuneracionMensual, 'CORRIDO');
    return Math.round(valorDia * diasPendientes);
}

/**
 * Calcula el impuesto único de segunda categoría (Art. 43 Ley de Impuesto a la Renta)
 * Tramos expresados en UTM, vigentes para trabajadores dependientes.
 * @param {number} baseImponible - Base imponible en pesos (remuneración - AFP - AFC - Salud)
 * @param {number} utm - Valor de la UTM del período en pesos
 * @returns {number} - Monto de impuesto único en pesos (0 si no aplica)
 */
function calcularImpuestoUnico(baseImponible, utm) {
    if (!utm || utm <= 0) return 0;
    const baseEnUTM = baseImponible / utm;
    const tramos = [
        { desde: 0,    hasta: 13.5,    factor: 0,     rebaja: 0 },
        { desde: 13.5, hasta: 30,       factor: 0.04,  rebaja: 0.54 },
        { desde: 30,   hasta: 50,       factor: 0.08,  rebaja: 1.74 },
        { desde: 50,   hasta: 70,       factor: 0.135, rebaja: 4.49 },
        { desde: 70,   hasta: 90,       factor: 0.23,  rebaja: 11.14 },
        { desde: 90,   hasta: 120,      factor: 0.304, rebaja: 17.8 },
        { desde: 120,  hasta: 310,      factor: 0.35,  rebaja: 23.32 },
        { desde: 310,  hasta: Infinity, factor: 0.40,  rebaja: 38.82 }
    ];
    for (const t of tramos) {
        if (baseEnUTM > t.desde && baseEnUTM <= t.hasta) {
            return Math.max(0, Math.round((baseEnUTM * t.factor - t.rebaja) * utm));
        }
    }
    return 0;
}

module.exports = {
    calcularAntiguedadExacta,
    calcularAniosCompletos,
    calcularMesesFraccion,
    calcularDiasEntreFechas,
    calcularValorDia,
    calcularValorHora,
    calcularRemuneracionProporcional,
    calcularGratificacionMensualArt50,
    calcularGratificacionProporcional,
    determinarDerechosSegunCausal,
    obtenerCausalesTermino,
    formatearMontoCLP,
    formatearRUT,
    formatearFechaChilena,
    formatearFechaCorta,
    calcularTope90UF,
    aplicarTope90UF,
    calcularDiasVacacionesDevengados,
    calcularMontoVacaciones,
    calcularImpuestoUnico
};
